import { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { IoStar } from 'react-icons/io5';
import { resolveShortLink, submitShortLinkReview } from '../../services/shortlinkService';

const CONTACT_PHONE = '070-7666-9413';

// 5점 별점 → rating_type 매핑
function starsToRating(n) {
  if (n <= 2) return 'unsatisfied';
  if (n === 3) return 'normal';
  return 'satisfied';
}

export default function ReviewPage() {
  const { serviceRequestId } = useParams();
  const [searchParams] = useSearchParams();
  const previewDone = searchParams.get('done') === '1';
  const previewMode = searchParams.get('preview') === '1' || String(serviceRequestId || '').startsWith('sample-');

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [link, setLink] = useState(null);

  const [stars, setStars] = useState(0);
  const [hover, setHover] = useState(0);
  const [memo, setMemo] = useState('');
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(previewDone);

  useEffect(() => {
    let alive = true;
    if (previewDone || previewMode) {
      setLink({ customerName: '이수영' });
      setLoading(false);
      return () => { alive = false; };
    }
    resolveShortLink(serviceRequestId)
      .then((d) => { if (alive) { setLink(d); setLoading(false); } })
      .catch((e) => { if (alive) { setLoadError(e.message); setLoading(false); } });
    return () => { alive = false; };
  }, [serviceRequestId, previewDone, previewMode]);

  const handleSubmit = async () => {
    if (!stars) { setFormError('별점을 선택해주세요.'); return; }
    setSubmitting(true);
    setFormError('');
    try {
      if (!previewMode && !previewDone) {
        await submitShortLinkReview({
          serviceRequestId,
          rating: starsToRating(stars),
          memo: memo.trim(),
        });
      }
      setDone(true);
    } catch (e) {
      setFormError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <Page><Centered><Muted>불러오는 중...</Muted></Centered></Page>;
  }

  if (loadError) {
    return (
      <Page>
        <PageWrap>
          <Header />
          <Card>
            <Title>안내</Title>
            <Muted>{loadError}</Muted>
            <FooterNote>
              문의 <a href={`tel:${CONTACT_PHONE.replace(/-/g, '')}`}>{CONTACT_PHONE}</a>
            </FooterNote>
          </Card>
        </PageWrap>
      </Page>
    );
  }

  if (done) {
    return (
      <Page>
        <PageWrap>
          <Header />
          <DoneCard>
            <DoneIllust src="/illust-submit-complete.png" alt="" />
            <DoneSub>평가 제출이 완료되어</DoneSub>
            <DoneTitle>정상적으로 접수되셨습니다</DoneTitle>
            <DoneDesc>소중한 의견 감사합니다. 더 나은 서비스로 보답하겠습니다.</DoneDesc>
          </DoneCard>
          <SecondaryBtn type="button" onClick={() => window.close()}>
            닫기
          </SecondaryBtn>
          <FooterNote>
            문의 <a href={`tel:${CONTACT_PHONE.replace(/-/g, '')}`}>{CONTACT_PHONE}</a>
          </FooterNote>
        </PageWrap>
      </Page>
    );
  }

  return (
    <Page>
      <PageWrap>
        <Header />
        <Card>
          <CardTitle>이용해주셔서 감사합니다.</CardTitle>
          <CardDesc>
            소중한 의견은 서비스 개선에 큰 도움이 됩니다.<br />
            간단히 평가를 남겨주세요.
          </CardDesc>

          <StarRow>
            {[1, 2, 3, 4, 5].map((n) => {
              const active = n <= (hover || stars);
              return (
                <StarBtn
                  key={n}
                  type="button"
                  onClick={() => setStars(n)}
                  onMouseEnter={() => setHover(n)}
                  onMouseLeave={() => setHover(0)}
                  aria-label={`${n}점`}
                >
                  <IoStar color={active ? '#F59E0B' : '#E5E7EB'} size={32} />
                </StarBtn>
              );
            })}
          </StarRow>

          <Textarea
            rows={3}
            value={memo}
            placeholder="평가를 남겨주세요."
            onChange={(e) => setMemo(e.target.value)}
          />

          {formError && <ErrorText>{formError}</ErrorText>}

          <BtnRow>
            <PrimaryBtn type="button" onClick={handleSubmit} disabled={submitting}>
              {submitting ? '제출 중...' : '리뷰 제출하기'}
            </PrimaryBtn>
            <SecondaryBtn type="button" onClick={() => window.close()}>
              닫기
            </SecondaryBtn>
          </BtnRow>
        </Card>

        <FooterNote>
          문의 <a href={`tel:${CONTACT_PHONE.replace(/-/g, '')}`}>{CONTACT_PHONE}</a>
        </FooterNote>
      </PageWrap>
    </Page>
  );
}

function Header() {
  return (
    <HeaderWrap>
      <LogoText>이음 <small>IEUM</small></LogoText>
    </HeaderWrap>
  );
}

const Page = styled.div`
  min-height: 100vh;
  background: #EEF2FF;
  display: flex;
  justify-content: center;
  padding: 16px 14px 40px;
`;

const PageWrap = styled.div`
  width: 100%;
  max-width: 480px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Centered = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
`;

const HeaderWrap = styled.div`
  padding: 6px 4px 8px;
`;

const LogoText = styled.div`
  font-size: 18px;
  font-weight: 800;
  color: #4A6CF7;
  letter-spacing: -0.5px;
  small {
    font-size: 9px;
    font-weight: 600;
    color: #4A6CF7;
    margin-left: 2px;
    letter-spacing: 1px;
  }
`;

const Card = styled.div`
  background: #fff;
  border-radius: 16px;
  padding: 24px 22px;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.04);
`;

const Title = styled.h1`
  font-size: 18px;
  font-weight: 800;
  color: #1F2937;
  margin: 0 0 8px;
  text-align: center;
`;

const CardTitle = styled.h1`
  font-size: 20px;
  font-weight: 800;
  color: #1F2937;
  margin: 0 0 8px;
`;

const CardDesc = styled.p`
  font-size: 12px;
  color: #6B7280;
  line-height: 1.6;
  margin: 0 0 20px;
`;

const StarRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  margin: 8px 0 22px;
`;

const StarBtn = styled.button`
  background: transparent;
  border: 0;
  padding: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
`;

const Textarea = styled.textarea`
  border: 1px solid #E5E7EB;
  border-radius: 10px;
  padding: 12px 14px;
  font-size: 13px;
  color: #1F2937;
  width: 100%;
  box-sizing: border-box;
  resize: none;
  font-family: inherit;
  background: #fff;

  &::placeholder { color: #9CA3AF; }
  &:focus {
    outline: none;
    border-color: #4A6CF7;
  }
`;

const ErrorText = styled.p`
  font-size: 13px;
  color: #DC2626;
  margin: 8px 0 0;
  text-align: center;
`;

const BtnRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin-top: 14px;
`;

const PrimaryBtn = styled.button`
  min-height: 48px;
  border: 0;
  border-radius: 12px;
  background: #4A6CF7;
  color: #fff;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;

  &:hover:not(:disabled) { background: #3F5BD9; }
  &:disabled { opacity: 0.6; cursor: default; }
`;

const SecondaryBtn = styled.button`
  min-height: 48px;
  border: 1px solid #E5E7EB;
  border-radius: 12px;
  background: #fff;
  color: #1F2937;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;

  &:hover { background: #F9FAFB; }
`;

const DoneCard = styled.div`
  background: #fff;
  border-radius: 16px;
  padding: 30px 24px;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.04);
  text-align: center;
`;

const DoneIllust = styled.img`
  width: 180px;
  height: 180px;
  margin: 0 auto 16px;
  display: block;
  object-fit: contain;
`;

const DoneSub = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #1F2937;
  margin-bottom: 4px;
`;

const DoneTitle = styled.div`
  font-size: 18px;
  font-weight: 800;
  color: #1F2937;
  margin-bottom: 10px;
`;

const DoneDesc = styled.div`
  font-size: 12px;
  color: #6B7280;
  line-height: 1.6;
`;

const Muted = styled.p`
  font-size: 14px;
  color: #6B7280;
  line-height: 1.6;
  text-align: center;
  margin: 0;
`;

const FooterNote = styled.p`
  margin: 14px 0 0;
  font-size: 12px;
  color: #9CA3AF;
  text-align: center;

  a {
    color: #4A6CF7;
    font-weight: 700;
    text-decoration: none;
  }
`;
